import { Component } from '@theme/component';

/** @type {WeakMap<ParentNode, number>} */
const sectionPlaybackSpeed = new WeakMap();

/** @type {WeakMap<object, boolean>} */
const swiperPlayPreference = new WeakMap();

/**
 * @typedef {Object} VideoCardRefs
 * @property {HTMLVideoElement} videoElement - The video element
 * @property {HTMLButtonElement} playButton - The play/pause toggle button
 * @property {HTMLButtonElement} [muteButton] - Optional mute/unmute toggle button
   * @property {HTMLElement} [scrubberContainer] - Optional progress scrubber container
 * Supports native video elements with custom play/mute controls.
 *
 * @extends {Component<VideoCardRefs>}
 */
class VideoCard extends Component {
  /** @type {IntersectionObserver|null} */
  #intersectionObserver = null;

  /** @type {boolean} */
  #isVisible = true;

  /** @type {EventListener|null} */
  #boundHandleVideoPlaying = null;

  /** @type {HTMLVideoElement|null} */
  #videoElement = null;

  /** @type {HTMLButtonElement|null} */
  #playButton = null;

  /** @type {HTMLButtonElement|null} */
  #muteButton = null;

  /** @type {HTMLElement|null} */
  #speedControl = null;

  /** @type {HTMLButtonElement|null} */
  #speedButton = null;

  /** @type {HTMLElement|null} */
  #speedMenu = null;

  /** @type {HTMLButtonElement[]} */
  #speedOptions = [];

  /** @type {HTMLButtonElement|null} */
  #fullscreenButton = null;

  /** @type {EventListener|null} */
  #boundHandleFullscreenClick = null;

  /** @type {EventListener|null} */
  #boundFullscreenChange = null;

  /** @type {EventListener} */
  #boundHandleSpeedClick = () => {};

  /** @type {EventListener} */
  #boundHandleSpeedOptionClick = () => {};

  /** @type {EventListener} */
  #boundHandleDocumentClick = () => {};

  /** @type {EventListener} */
  #boundHandleDocumentKeydown = () => {};

  /** @type {boolean} */
  #observerEnabled = true;

  /** @type {boolean} */
  #isInitialized = false;

  /** @type {HTMLElement|null} */
  #scrubberContainer = null;

  /** @type {EventListener|null} */
  #boundHandleVideoElementClick = null;

  /** @type {EventListener|null} */
  #boundHandleVideoWaiting = null;

  /** @type {EventListener|null} */
  #boundHandleVideoReady = null;

  /** @type {EventListener|null} */
  #boundHandleVideoProgress = null;

  /** @type {EventListener|null} */
  #boundHandlePlaybackStateChange = null;

  /** @type {number|null} */
  #playbackMonitorInterval = null;

  /** @type {number} */
  #playRequestTimestamp = 0;

  /** @type {number} */
  #lastPlaybackPosition = 0;

  /** @type {boolean} */
  #hasSeenPlaybackAdvance = false;

  /** @type {EventListener|null} */
  #boundHandleScrubberClick = null;

  /** @type {number|null} */
  #bufferingStartTimestamp = null;

  /** @type {number} */
  #loadingDisplayDelayMs = 350;

  connectedCallback() {
    super.connectedCallback();

    // Find video element with fallback for timing issues
    if (!this.#videoElement) {
      this.#videoElement = this.querySelector('video');
    }

    // If video element not found immediately, wait for it
    if (!this.#videoElement) {
      const observer = new MutationObserver(() => {
        if (!this.#videoElement) {
          this.#videoElement = this.querySelector('video');

          if (this.#videoElement) {
            observer.disconnect();
            this.#initializeComponent();
          }
        }
      });

      observer.observe(this, { childList: true, subtree: true });

      // Also try again after a short delay in case mutation observer doesn't catch it
      setTimeout(() => {
        if (!this.#videoElement) {
          this.#videoElement = this.querySelector('video');
        }
        observer.disconnect();
        this.#initializeComponent();
      }, 100);
    } else {
      this.#initializeComponent();
    }
  }

  /**
   * Initialize the component once video element is found
   */
  #initializeComponent() {
    if (this.#isInitialized) {
      return;
    }

    // Store refs as private properties using data attributes
    this.#playButton = this.querySelector('[data-bon-play-button]');
    this.#muteButton = this.querySelector('[data-bon-mute-button]');
    this.#speedControl = this.querySelector('[data-bon-speed-control]');
    this.#speedButton = this.querySelector('[data-bon-speed-button]');
    this.#speedMenu = this.querySelector('[data-bon-speed-menu]');
    this.#speedOptions = Array.from(this.querySelectorAll('[data-bon-speed-option]'));
    this.#fullscreenButton = this.querySelector('[data-bon-fullscreen-button]');
    this.#scrubberContainer = this.querySelector('[data-bon-scrubber]');

    if (!this.#videoElement || !this.#playButton) {
      return;
    }

    if (this.#muteButton) {
      this.#muteButton.hidden = true;
      this.#updateMuteIcon();
    }

    if (this.#speedControl) {
      this.#speedControl.hidden = true;
    }

    this.#setupSpeedControl();

    if (this.#fullscreenButton) {
      this.#fullscreenButton.hidden = true;
      this.#boundHandleFullscreenClick = this.handleFullscreenClick.bind(this);
      this.#fullscreenButton.addEventListener('click', this.#boundHandleFullscreenClick);

      this.#boundFullscreenChange = this.#handleFullscreenChange.bind(this);
      document.addEventListener('fullscreenchange', this.#boundFullscreenChange);

      // iOS Safari uses webkit fullscreen events on the video element
      if (this.#videoElement) {
        this.#videoElement.addEventListener('webkitbeginfullscreen', this.#boundFullscreenChange);
        this.#videoElement.addEventListener('webkitendfullscreen', this.#boundFullscreenChange);
      }

      // Keep the button label/icon in sync if user exits fullscreen with ESC
      this.#handleFullscreenChange();
    }

    this.#setupIntersectionObserver();
    this.#boundHandleVideoPlaying = this.#handleVideoPlaying.bind(this);
    if (this.#boundHandleVideoPlaying) {
      document.addEventListener('video:playing', this.#boundHandleVideoPlaying);
    }

    // Allow clicking on video element to play/pause
    this.#boundHandleVideoElementClick = this.handlePlayClick.bind(this);
    if (this.#boundHandleVideoElementClick) {
      this.#videoElement.addEventListener('click', this.#boundHandleVideoElementClick);
    }

    // Scrubber click/seeking support
    if (this.#scrubberContainer) {
      this.#boundHandleScrubberClick = this.handleScrubberClick.bind(this);
      this.#scrubberContainer.addEventListener('click', this.#boundHandleScrubberClick);
    }

    this.#boundHandleVideoWaiting = this.#handleVideoWaiting.bind(this);
    this.#boundHandleVideoReady = this.#handleVideoReady.bind(this);
    this.#boundHandleVideoProgress = this.#handleVideoProgress.bind(this);
    this.#boundHandlePlaybackStateChange = this.#handlePlaybackStateChange.bind(this);

    if (this.#boundHandleVideoWaiting) {
      this.#videoElement.addEventListener('loadstart', this.#boundHandleVideoWaiting);
      this.#videoElement.addEventListener('waiting', this.#boundHandleVideoWaiting);
      this.#videoElement.addEventListener('stalled', this.#boundHandleVideoWaiting);
    }

    if (this.#boundHandleVideoProgress) {
      this.#videoElement.addEventListener('progress', this.#boundHandleVideoProgress);
      this.#videoElement.addEventListener('timeupdate', this.#boundHandleVideoProgress);
      this.#videoElement.addEventListener('seeking', this.#boundHandleVideoProgress);
      this.#videoElement.addEventListener('seeked', this.#boundHandleVideoProgress);
    }

    if (this.#boundHandlePlaybackStateChange) {
      this.#videoElement.addEventListener('play', this.#boundHandlePlaybackStateChange);
      this.#videoElement.addEventListener('pause', this.#boundHandlePlaybackStateChange);
      this.#videoElement.addEventListener('ended', this.#boundHandlePlaybackStateChange);
    }

    if (this.#boundHandleVideoReady) {
      this.#videoElement.addEventListener('playing', this.#boundHandleVideoReady);
      this.#videoElement.addEventListener('error', this.#boundHandleVideoReady);
    }

    // Setup Swiper slide change detection for mobile auto-play
    this.#setupSwiperSlideDetection();
    this.#isInitialized = true;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#cleanup();
  }

  /**
   * Handle play button click - toggle video playback and manage sibling videos
   * @param {Event} event
   */
  handlePlayClick(event) {
    event.preventDefault();

    if (!this.#videoElement) {
      return;
    }

    const isCurrentlyPlaying = !this.#videoElement.paused;

    if (isCurrentlyPlaying) {
      this.#setSwiperPlayPreference(false);
      this.#pauseVideo();
    } else {
      this.#setSwiperPlayPreference(true);
      this.#playVideo();
      this.#pauseSiblingVideos();

      // Dispatch custom event so other video cards know to pause
      this.dispatchEvent(new CustomEvent('video:playing', {
        detail: { videoElement: this.#videoElement },
        bubbles: true
      }));
    }
  }

  /**
   * Play the video and update UI
   */
  #playVideo() {
    if (!this.#videoElement) {
      return;
    }

    // Keep playback speed consistent across video cards in the same section.
    this.#setPlaybackSpeed(this.#getStoredPlaybackSpeed());

    this.#playRequestTimestamp = Date.now();
    this.#lastPlaybackPosition = this.#videoElement.currentTime;
    this.#hasSeenPlaybackAdvance = false;
    this.#bufferingStartTimestamp = this.#playRequestTimestamp;
    this.#setLoadingState(false);
    this.#applyPlayingUi();

    this.#videoElement.play().catch((err) => {
      this.#setLoadingState(false);
      this.#applyPausedUi();
      console.warn('Video autoplay was prevented:', err);
    });
  }

  /**
   * Pause the video and update UI
   */
  #pauseVideo() {
    if (!this.#videoElement) return;

    this.#videoElement.pause();
    this.#applyPausedUi();
  }

  /**
   * Apply playing chrome (icons/controls) without changing media playback.
   */
  #applyPlayingUi() {
    this.classList.add('is-playing');
    this.#playButton?.classList.add('is-playing');
    this.#startPlaybackMonitor();

    if (this.#muteButton) {
      this.#muteButton.hidden = false;
      this.#updateMuteIcon();
    }

    if (this.#speedControl) {
      this.#speedControl.hidden = false;
    }

    if (this.#fullscreenButton) {
      this.#fullscreenButton.hidden = false;
    }
  }

  /**
   * Apply paused chrome (icons/controls) without changing media playback.
   */
  #applyPausedUi() {
    this.#stopPlaybackMonitor();
    this.#bufferingStartTimestamp = null;
    this.#setLoadingState(false);

    this.classList.remove('is-playing');
    this.#playButton?.classList.remove('is-playing');

    if (this.#muteButton) {
      this.#muteButton.hidden = true;
      this.#updateMuteIcon();
    }

    this.#hideSpeedMenu();
    if (this.#speedControl) {
      this.#speedControl.hidden = true;
    }

    if (this.#fullscreenButton) {
      this.#fullscreenButton.hidden = true;
    }
  }

  /**
   * Keep custom controls in sync when the browser pauses/plays the element
   * (e.g. exiting iOS native fullscreen).
   */
  #syncUiToPlaybackState() {
    if (!this.#videoElement) {
      return;
    }

    // Use paused only — do not treat ended as paused. Looping clips can fire
    // ended while playback continues, and browsers often omit a follow-up play.
    if (this.#videoElement.paused) {
      this.#applyPausedUi();
    } else {
      this.#applyPlayingUi();
    }

    this.#syncLoadingStateFromPlayback();
  }

  /**
   * Handle native play/pause so UI cannot drift from the media element.
   * @param {Event} [event]
   */
  #handlePlaybackStateChange(event) {
    if (event?.type === 'ended' && this.#videoElement?.loop) {
      // Loop restart: keep playing chrome even if paused flickered at the seam.
      this.#applyPlayingUi();
      this.#syncLoadingStateFromPlayback();
      return;
    }

    this.#syncUiToPlaybackState();
  }

  /**
   * Initialize speed controls and bind events
   */
  #setupSpeedControl() {
    if (!this.#videoElement || !this.#speedButton || !this.#speedMenu || !this.#speedOptions?.length) {
      return;
    }

    this.#setPlaybackSpeed(this.#getStoredPlaybackSpeed());

    this.#boundHandleSpeedClick = this.#handleSpeedClick.bind(this);
    this.#boundHandleSpeedOptionClick = this.#handleSpeedOptionClick.bind(this);
    this.#boundHandleDocumentClick = this.#handleDocumentClick.bind(this);
    this.#boundHandleDocumentKeydown = this.#handleDocumentKeydown.bind(this);

    this.#speedButton.addEventListener('click', this.#boundHandleSpeedClick);
    this.#speedMenu.addEventListener('click', this.#boundHandleSpeedOptionClick);
    document.addEventListener('click', this.#boundHandleDocumentClick);
    document.addEventListener('keydown', this.#boundHandleDocumentKeydown);
  }

  /**
   * Toggle speed dropdown panel
   * @param {Event} event
   */
  #handleSpeedClick(event) {
    event.preventDefault();
    event.stopPropagation();

    if (this.#speedControl?.hidden) {
      return;
    }

    if (this.#speedMenu?.hidden) {
      this.#showSpeedMenu();
    } else {
      this.#hideSpeedMenu();
    }
  }

  /**
   * Handle selecting a playback speed option
   * @param {Event} event
   */
  #handleSpeedOptionClick(event) {
    const option = event.target instanceof Element
      ? event.target.closest('[data-bon-speed-option]')
      : null;

    if (!option) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const speed = Number.parseFloat(option.getAttribute('data-speed') || '1');
    if (!Number.isFinite(speed)) {
      return;
    }

    this.#setPlaybackSpeed(speed);
    this.#hideSpeedMenu();
  }

  /**
   * Close speed menu on outside click
   * @param {Event} event
   */
  #handleDocumentClick(event) {
    if (this.#speedMenu?.hidden) {
      return;
    }

    const target = event.target;
    if (target instanceof Node && this.#speedControl?.contains(target)) {
      return;
    }

    this.#hideSpeedMenu();
  }

  /**
   * Close speed menu with Escape key
   * @param {KeyboardEvent} event
   */
  #handleDocumentKeydown(event) {
    if (this.#speedMenu?.hidden) {
      return;
    }

    if (event.key === 'Escape') {
      this.#hideSpeedMenu();
      this.#speedButton?.focus();
      return;
    }

    if (!this.#speedOptions.length) {
      return;
    }

    const currentIndex = this.#speedOptions.findIndex(option => option.getAttribute('aria-checked') === 'true');
    const safeCurrentIndex = currentIndex >= 0 ? currentIndex : 0;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      const nextIndex = (safeCurrentIndex + 1) % this.#speedOptions.length;
      this.#speedOptions[nextIndex]?.focus();
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      const prevIndex = (safeCurrentIndex - 1 + this.#speedOptions.length) % this.#speedOptions.length;
      this.#speedOptions[prevIndex]?.focus();
      return;
    }

    if (event.key === 'Home') {
      event.preventDefault();
      this.#speedOptions[0]?.focus();
      return;
    }

    if (event.key === 'End') {
      event.preventDefault();
      this.#speedOptions[this.#speedOptions.length - 1]?.focus();
    }
  }

  /**
   * Show speed options menu
   */
  #showSpeedMenu() {
    if (!this.#speedMenu || !this.#speedButton || this.#speedControl?.hidden) {
      return;
    }

    this.#speedMenu.hidden = false;
    this.#speedButton.setAttribute('aria-expanded', 'true');
    const selectedOption = this.#speedOptions.find(option => option.getAttribute('aria-checked') === 'true');
    selectedOption?.focus();
  }

  /**
   * Hide speed options menu
   */
  #hideSpeedMenu() {
    if (!this.#speedMenu || !this.#speedButton) {
      return;
    }

    this.#speedMenu.hidden = true;
    this.#speedButton.setAttribute('aria-expanded', 'false');
  }

  /**
   * Set playback speed and sync UI
   * @param {number} speed
   */
  #setPlaybackSpeed(speed) {
    if (!this.#videoElement) {
      return;
    }

    this.#videoElement.playbackRate = speed;
    this.#setStoredPlaybackSpeed(speed);

    if (this.#speedButton) {
      this.#speedButton.textContent = `${Number.isInteger(speed) ? speed.toFixed(0) : speed.toFixed(1)}x`;
    }

    for (const option of this.#speedOptions) {
      const optionSpeed = Number.parseFloat(option.getAttribute('data-speed') || '1');
      const isSelected = Math.abs(optionSpeed - speed) < 0.01;
      option.setAttribute('aria-checked', isSelected ? 'true' : 'false');
    }
  }

  /**
   * Handle buffering/loading events from the video element
   */
  #handleVideoWaiting() {
    if (!this.classList.contains('is-playing') || this.#videoElement?.paused) {
      this.#bufferingStartTimestamp = null;
      this.#setLoadingState(false);
      return;
    }

    if (this.#bufferingStartTimestamp === null) {
      this.#bufferingStartTimestamp = Date.now();
    }

    this.#syncLoadingStateFromPlayback();
  }

  /**
   * Keep loading state visible while playback has been requested but not yet started
   */
  #handleVideoProgress() {
    this.#syncLoadingStateFromPlayback();

    // update scrubber progress
    if (this.#scrubberContainer && this.#videoElement && this.#videoElement.duration) {
      const percent = (this.#videoElement.currentTime / this.#videoElement.duration) * 100;
      const scrubber = this.#scrubberContainer.querySelector('.bon-review-card__scrubber');
      if (scrubber) scrubber.style.width = `${percent}%`;
    }
  }

  /**
   * Handle click on scrubber to seek video
   * @param {MouseEvent} event
   */
  handleScrubberClick(event) {
    if (!this.#videoElement || !this.#scrubberContainer) return;

    const rect = this.#scrubberContainer.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    const percent = Math.max(0, Math.min(1, clickX / rect.width));
    this.#videoElement.currentTime = percent * this.#videoElement.duration;
  }

  /**
   * Handle readiness/playback events from the video element
   */
  #handleVideoReady() {
    this.#syncLoadingStateFromPlayback();
  }

  /**
   * Toggle loading visual state on play button
   * @param {boolean} isLoading
   */
  #setLoadingState(isLoading) {
    this.#playButton?.classList.toggle('is-loading', Boolean(isLoading));
  }

  /**
   * Start periodic playback checks so loading state reflects actual frame advancement
   */
  #startPlaybackMonitor() {
    this.#stopPlaybackMonitor();

    this.#playbackMonitorInterval = window.setInterval(() => {
      this.#syncLoadingStateFromPlayback();
    }, 200);
  }

  /**
   * Stop periodic playback checks
   */
  #stopPlaybackMonitor() {
    if (this.#playbackMonitorInterval !== null) {
      window.clearInterval(this.#playbackMonitorInterval);
      this.#playbackMonitorInterval = null;
    }
  }

  /**
   * Show loading while awaiting the first frame after play, or while truly
   * buffering/seeking. Do not treat every tiny stall after playback has
   * started as loading (that caused stuck spinners previously).
   */
  #syncLoadingStateFromPlayback() {
    if (!this.#videoElement) {
      return;
    }

    const isRequestedToPlay = this.classList.contains('is-playing');
    const hasFullyEnded = this.#videoElement.ended && !this.#videoElement.loop;

    if (!isRequestedToPlay || this.#videoElement.paused || hasFullyEnded) {
      this.#bufferingStartTimestamp = null;
      this.#setLoadingState(false);
      return;
    }

    const currentTime = this.#videoElement.currentTime;
    const hasAdvanced = currentTime > this.#lastPlaybackPosition + 0.01;

    if (hasAdvanced) {
      this.#hasSeenPlaybackAdvance = true;
      this.#lastPlaybackPosition = currentTime;
      this.#bufferingStartTimestamp = null;
      this.#setLoadingState(false);
      return;
    }

    const isBuffering =
      this.#videoElement.seeking ||
      this.#videoElement.readyState < HTMLMediaElement.HAVE_FUTURE_DATA;
    const awaitingFirstFrame = !this.#hasSeenPlaybackAdvance;
    const isPending = awaitingFirstFrame || isBuffering;

    if (!isPending) {
      this.#bufferingStartTimestamp = null;
      this.#setLoadingState(false);
      return;
    }

    if (this.#bufferingStartTimestamp === null) {
      this.#bufferingStartTimestamp = Date.now();
    }

    this.#setLoadingState(Date.now() - this.#bufferingStartTimestamp >= this.#loadingDisplayDelayMs);
  }

  /**
   * Setup Intersection Observer to pause video when <50% visible
   * Runs on all breakpoints so mobile scroll-out also pauses video
   */
  #setupIntersectionObserver() {
    const options = {
      threshold: 0.5 // Trigger when less than 50% visible
    };

    this.#intersectionObserver = new IntersectionObserver((entries) => {
      // Skip observer logic if we're handling Swiper slide changes
      if (!this.#observerEnabled) {
        return;
      }

      for (const entry of entries) {
        this.#isVisible = entry.isIntersecting && entry.intersectionRatio >= 0.5;

        // Pause if video is playing and becomes out of view
        if (!this.#isVisible && !this.#videoElement?.paused) {
          this.#pauseVideo();
        }
      }
    }, options);

    this.#intersectionObserver.observe(this);
  }

  /**
   * Find and pause all other videos on the page
   */
  #pauseSiblingVideos() {
    const videoGroupRoot = this.#getVideoGroupRoot();
    const allVideoCards = videoGroupRoot.querySelectorAll('video-card');

    for (const videoCard of allVideoCards) {
      if (videoCard !== this) {
        videoCard.classList.remove('is-playing');
        const playButton = videoCard.querySelector('[data-bon-play-button]');
        if (playButton) {
          playButton.classList.remove('is-playing');
          playButton.classList.remove('is-loading');
        }

        const muteButton = videoCard.querySelector('[data-bon-mute-button]');
        if (muteButton) {
          muteButton.setAttribute('hidden', '');
        }

        const speedControl = videoCard.querySelector('[data-bon-speed-control]');
        if (speedControl) {
          speedControl.setAttribute('hidden', '');
        }

        const speedButton = videoCard.querySelector('[data-bon-speed-button]');
        if (speedButton) {
          speedButton.setAttribute('aria-expanded', 'false');
        }

        const speedMenu = videoCard.querySelector('[data-bon-speed-menu]');
        if (speedMenu) {
          speedMenu.setAttribute('hidden', '');
        }

        const video = videoCard.querySelector('video');
        if (video) {
          video.pause();
        }
      }
    }
  }

  /**
   * Handle video:playing event from other cards - pause self if different video playing
   * @param {CustomEvent} event
   */
  #handleVideoPlaying(event) {
    if (event.detail?.videoElement === this.#videoElement) {
      // Event is from our own video, ignore
      return;
    }

    const sourceVideoCard = event.target instanceof Element ? event.target.closest('video-card') : null;
    if (!sourceVideoCard) {
      return;
    }

    if (this.#getVideoGroupRoot(sourceVideoCard) !== this.#getVideoGroupRoot()) {
      return;
    }

    // Pause self if any other video is playing
    if (event.target !== this) {
      this.#pauseVideo();
    }
  }

  /**
   * Handle mute button click - toggle mute state
   * @param {Event} event
   */
  handleMuteClick(event) {
    event.preventDefault();

    if (!this.#videoElement || !this.#muteButton) {
      return;
    }

    this.#videoElement.muted = !this.#videoElement.muted;
    this.#updateMuteIcon();

    // Sync mute state to all other video cards on the page
    this.#syncMuteStateToOtherVideos();
  }

  /**
   * Whether this card's video is currently in fullscreen (standard or iOS webkit).
   * @returns {boolean}
   */
  #isVideoFullscreen() {
    const video = this.#videoElement;

    if (video?.webkitDisplayingFullscreen) {
      return true;
    }

    return Boolean(
      document.fullscreenElement &&
        (document.fullscreenElement === this ||
          document.fullscreenElement === video ||
          this.contains(document.fullscreenElement))
    );
  }

  /**
   * Handle fullscreen toggle button click
   * @param {Event} event
   */
  handleFullscreenClick(event) {
    event.preventDefault();

    if (!this.#fullscreenButton) {
      return;
    }

    const video = this.#videoElement;
    const isCurrentlyFullscreen = this.#isVideoFullscreen();

    if (isCurrentlyFullscreen) {
      if (typeof document.exitFullscreen === 'function' && document.fullscreenElement) {
        document.exitFullscreen();
      } else if (typeof video?.webkitExitFullscreen === 'function') {
        video.webkitExitFullscreen();
      }
    } else if (!video) {
      return;
    } else if (typeof video.requestFullscreen === 'function') {
      video.requestFullscreen().catch(() => {});
    } else if (
      typeof video.webkitEnterFullscreen === 'function' &&
      video.webkitSupportsFullscreen !== false
    ) {
      video.webkitEnterFullscreen();
    }
  }

  /**
   * Update fullscreen button icon/label when fullscreen state changes
   */
  #handleFullscreenChange() {
    if (!this.#fullscreenButton) {
      return;
    }

    const isFullscreen = this.#isVideoFullscreen();

    const enterIcon = this.#fullscreenButton.querySelector('.bon-video__icon--enter, .bon-review-card__icon--enter');
    const exitIcon = this.#fullscreenButton.querySelector('.bon-video__icon--exit, .bon-review-card__icon--exit');

    if (enterIcon) enterIcon.hidden = isFullscreen;
    if (exitIcon) exitIcon.hidden = !isFullscreen;

    this.#fullscreenButton.setAttribute(
      'aria-label',
      isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'
    );

    // iOS native fullscreen often pauses on exit; keep chrome matched to media.
    this.#syncUiToPlaybackState();
  }

  /**
   * Sync mute state to all other video cards on the page
   */
  #syncMuteStateToOtherVideos() {
    const videoGroupRoot = this.#getVideoGroupRoot();
    const allVideoCards = videoGroupRoot.querySelectorAll('video-card');
    const currentMutedState = this.#videoElement?.muted ?? false;

    for (const videoCard of allVideoCards) {
      if (videoCard !== this) {
        const video = videoCard.querySelector('video');
        if (video) {
          video.muted = currentMutedState;
        }

        // Update the icon for other videos if they're currently playing
        const muteButton = videoCard.querySelector('[data-bon-mute-button]');
        if (muteButton && !muteButton.hidden) {
          const unmuteIcon = muteButton.querySelector('[data-bon-unmute-icon], .bon-review-card__icon--unmute');
          const mutedIcon = muteButton.querySelector('[data-bon-muted-icon], .bon-review-card__icon--muted');

          if (unmuteIcon) unmuteIcon.hidden = !currentMutedState;
          if (mutedIcon) mutedIcon.hidden = currentMutedState;

          muteButton.setAttribute(
            'aria-label',
            currentMutedState ? 'Unmute video' : 'Mute video'
          );
        }
      }
    }
  }

  /**
   * Update mute icon based on current mute state
   */
  #updateMuteIcon() {
    if (!this.#muteButton || !this.#videoElement) return;

    const unmuteIcon = this.#muteButton.querySelector('[data-bon-unmute-icon], .bon-review-card__icon--unmute');
    const mutedIcon = this.#muteButton.querySelector('[data-bon-muted-icon], .bon-review-card__icon--muted');
    const isMuted = this.#videoElement?.muted ?? false;

    // When muted: show unmute icon (to unmute), hide mute icon
    // When unmuted: show mute icon (to mute), hide unmute icon
    if (unmuteIcon) unmuteIcon.hidden = !isMuted;
    if (mutedIcon) mutedIcon.hidden = isMuted;

    // Update aria-label
    this.#muteButton.setAttribute(
      'aria-label',
      isMuted ? 'Unmute video' : 'Mute video'
    );
  }

  /**
   * Setup Swiper slide change detection for mobile auto-play
   */
  #setupSwiperSlideDetection() {
    // Find the closest Swiper container
    const swiperContainer = this.closest('.swiper');

    if (!swiperContainer) {
      return;
    }

    // Get the swiper instance - it's stored as a property on the element
    let swiper = swiperContainer.swiper || swiperContainer.bonCarouselSwiper || swiperContainer.bonNewsSwiper || swiperContainer.bonModesSwiper;

    if (!swiper) {
      // Swiper might not be initialized yet, try again after a delay
      setTimeout(() => this.#setupSwiperSlideDetection(), 100);
      return;
    }

    if (!swiperPlayPreference.has(swiper)) {
      swiperPlayPreference.set(swiper, false);
    }

    // Helper function to check if we're on mobile
    const isMobileSize = () => window.innerWidth < 750;

    // Listen for Swiper slide changes (only on mobile)
    swiper.on('slideChange', () => {
      if (!isMobileSize()) {
        return;
      }

      // Disable observer during slide transitions to prevent interference
      this.#observerEnabled = false;

      const isActive = swiper.slides[swiper.activeIndex] && swiper.slides[swiper.activeIndex].contains(this);
      const shouldKeepPlaying = Boolean(swiperPlayPreference.get(swiper));

      if (!isActive) {
        // Pause when slide moves away from this card
        if (!this.#videoElement?.paused) {
          this.#pauseVideo();
        }
      } else if (shouldKeepPlaying && this.#videoElement?.paused) {
        this.#playVideo();
        this.#pauseSiblingVideos();

        this.dispatchEvent(new CustomEvent('video:playing', {
          detail: { videoElement: this.#videoElement },
          bubbles: true
        }));
      }

      // Re-enable observer after transition completes
      setTimeout(() => {
        this.#observerEnabled = true;
      }, 500);
    });
  }

  /**
   * Persist explicit user playback preference for this swiper instance.
   * @param {boolean} shouldPlay
   */
  #setSwiperPlayPreference(shouldPlay) {
    const swiper = this.#getSwiperInstance();

    if (!swiper) {
      return;
    }

    swiperPlayPreference.set(swiper, shouldPlay);
  }

  /**
   * Resolve swiper instance for this card, if any.
   * @returns {any|null}
   */
  #getSwiperInstance() {
    const swiperContainer = this.closest('.swiper');

    if (!swiperContainer) {
      return null;
    }

    return swiperContainer.swiper || swiperContainer.bonCarouselSwiper || swiperContainer.bonNewsSwiper || swiperContainer.bonModesSwiper || null;
  }

  /**
   * Cleanup observers and event listeners
   */
  #cleanup() {
    if (this.#scrubberContainer && this.#boundHandleScrubberClick) {
      this.#scrubberContainer.removeEventListener('click', this.#boundHandleScrubberClick);
      this.#boundHandleScrubberClick = null;
    }
    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = null;

    if (this.#boundHandleVideoPlaying) {
      document.removeEventListener('video:playing', this.#boundHandleVideoPlaying);
      this.#boundHandleVideoPlaying = null;
    }

    if (this.#videoElement && this.#boundHandleVideoElementClick) {
      this.#videoElement.removeEventListener('click', this.#boundHandleVideoElementClick);
      this.#boundHandleVideoElementClick = null;
    }

    if (this.#videoElement && this.#boundHandleVideoWaiting) {
      this.#videoElement.removeEventListener('loadstart', this.#boundHandleVideoWaiting);
      this.#videoElement.removeEventListener('waiting', this.#boundHandleVideoWaiting);
      this.#videoElement.removeEventListener('stalled', this.#boundHandleVideoWaiting);
      this.#boundHandleVideoWaiting = null;
    }

    if (this.#videoElement && this.#boundHandleVideoProgress) {
      this.#videoElement.removeEventListener('progress', this.#boundHandleVideoProgress);
      this.#videoElement.removeEventListener('timeupdate', this.#boundHandleVideoProgress);
      this.#videoElement.removeEventListener('seeking', this.#boundHandleVideoProgress);
      this.#videoElement.removeEventListener('seeked', this.#boundHandleVideoProgress);
      this.#boundHandleVideoProgress = null;
    }

    if (this.#videoElement && this.#boundHandlePlaybackStateChange) {
      this.#videoElement.removeEventListener('play', this.#boundHandlePlaybackStateChange);
      this.#videoElement.removeEventListener('pause', this.#boundHandlePlaybackStateChange);
      this.#videoElement.removeEventListener('ended', this.#boundHandlePlaybackStateChange);
      this.#boundHandlePlaybackStateChange = null;
    }

    if (this.#videoElement && this.#boundHandleVideoReady) {
      this.#videoElement.removeEventListener('playing', this.#boundHandleVideoReady);
      this.#videoElement.removeEventListener('error', this.#boundHandleVideoReady);
      this.#boundHandleVideoReady = null;
    }

    if (this.#boundFullscreenChange) {
      document.removeEventListener('fullscreenchange', this.#boundFullscreenChange);

      if (this.#videoElement) {
        this.#videoElement.removeEventListener('webkitbeginfullscreen', this.#boundFullscreenChange);
        this.#videoElement.removeEventListener('webkitendfullscreen', this.#boundFullscreenChange);
      }

      this.#boundFullscreenChange = null;
    }

    if (this.#speedButton) {
      this.#speedButton.removeEventListener('click', this.#boundHandleSpeedClick);
    }

    if (this.#speedMenu) {
      this.#speedMenu.removeEventListener('click', this.#boundHandleSpeedOptionClick);
    }

    document.removeEventListener('click', this.#boundHandleDocumentClick);

    document.removeEventListener('keydown', this.#boundHandleDocumentKeydown);

    if (this.#fullscreenButton && this.#boundHandleFullscreenClick) {
      this.#fullscreenButton.removeEventListener('click', this.#boundHandleFullscreenClick);
      this.#boundHandleFullscreenClick = null;
    }

    this.#stopPlaybackMonitor();
    this.#bufferingStartTimestamp = null;

    this.#isInitialized = false;
  }

  /**
   * Scope synchronization to the closest Shopify section so behavior is local, not page-global.
   * @param {Element|this} [element]
   * @returns {ParentNode}
   */
  #getVideoGroupRoot(element = this) {
    if (element instanceof Element) {
      return element.closest('[id^="shopify-section-"]') || document;
    }

    return document;
  }

  /**
   * Get last selected speed for this section.
   * @returns {number}
   */
  #getStoredPlaybackSpeed() {
    const root = this.#getVideoGroupRoot();
    const stored = sectionPlaybackSpeed.get(root);

    if (typeof stored === 'number' && Number.isFinite(stored)) {
      return stored;
    }

    return 1;
  }

  /**
   * Store selected speed for this section.
   * @param {number} speed
   */
  #setStoredPlaybackSpeed(speed) {
    const root = this.#getVideoGroupRoot();
    sectionPlaybackSpeed.set(root, speed);
  }
}

if (!customElements.get('video-card')) {
  customElements.define('video-card', VideoCard);
}
